import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import { Modal } from '@unified-checkout/design-system';

import type { IDeleteEmailProps } from '@app/dashboard/types/innerLayout/notifications/modals/remove-email';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { NOTIFICATION_CATEGORIES } from '@app/dashboard/constants/settings/notifications';
import { deleteEmail } from '@app/dashboard/services/inner_layout/notifications';
import { notify } from '@app/dashboard/utils/notifier';
import { useLangStore } from '@app/dashboard/store/lang';

const DeleteEmail = ({
  isModalOpen,
  emailToDelete,
  setIsModalOpen,
}: IDeleteEmailProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const screenWidth = useScreenWidth();

  const { lang } = useLangStore();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const { mutate: deleteEmailMutation, isPending: isDeleteEmailLoading } =
    useMutation({
      mutationFn: (payload: { email: string; category: string }) =>
        deleteEmail(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_EMAILS] });
        notify.success(t('innerLayout.notificationsPage.toaster.emailRemoved'));
        setIsModalOpen(false);
      },
      onError: (axiosErr: AxiosError<{ message?: string; en?: string, ar?: string }>) => {
        let message = t('errors.somethingWentWrong');        

        if (axiosErr.response?.data?.message) {
          message = axiosErr.response?.data?.message;
        } else if (axiosErr.response?.data?.[lang]) {
          message = axiosErr.response.data[lang];
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }
        notify.error(message);
      },
    });

  const handleClick = () => {
    deleteEmailMutation({
      email: emailToDelete as string,
      category: NOTIFICATION_CATEGORIES.NOTIFICATION,
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <Modal
      isOverlayAboveModal
      isMobile={isMobile}
      closeOnClickOutside={false}
      open={isModalOpen}
      title={t('innerLayout.notificationsPage.modals.confirmDelete.title', {
        email: emailToDelete,
      })}
      btnsProps={[
        {
          size: 'md',
          color: 'danger',
          variant: 'contained',
          text: t('innerLayout.notificationsPage.modals.actions.yesDelete'),
          onClickHandler: handleClick,
          loading: isDeleteEmailLoading,
          disabled: isDeleteEmailLoading,
        },
        {
          size: 'md',
          color: 'secondary',
          variant: 'outlined',
          text: t('innerLayout.notificationsPage.modals.actions.cancel'),
          onClickHandler: handleCancel,
          disabled: isDeleteEmailLoading,
        },
      ]}
    />
  );
};

export default DeleteEmail;
